import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getJukebox } from '../jukebox-assets.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La photo porte sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'jukebox',
  family: 'photographic',
  danceability: 0.72,
  energyLevel: 0.66,
  bpmMin: 90,
  bpmMax: 160,
  movement: 0.55,
  tonality: 0.80,
  enabled: true
};

// JUKEBOX : un juke-box des années 50 dans un diner sombre, d'après deux
// photos identiques, éteint et allumé (tools/build-jukebox.mjs, R8). Il joue
// la pochette : un 45 tours tourne derrière la vitre, des bulles montent dans
// les tubes, les panneaux de l'arche s'allument au rythme. Rock'n'roll, soul,
// funk, rockabilly, pop rétro, disco. Règles R1 à R17 ; rythme « coupes sur
// la phrase » (R9 : danceability 0,72).
//
// LA LUMIÈRE, par pixel : off + diff x intensité(zone, hauteur). Les zones
// sont celles du build (fenêtre, arche G/D, tubes G/D, grille, divers,
// ambiance) :
//   ARCHE (les panneaux)  chenillard montant sur chaque mesure, le long de
//                         l'arche (coordonnée 0 au pied, 1 au fronton), et
//                         roue des couleurs : à chaque phrase la lumière de
//                         l'arche passe en une mesure à la teinte chaude
//                         suivante (luminance conservée) ; la teinte « 0 »
//                         est celle de la photo
//   TUBES                 le liquide lumineux, constant ; les bulles dessus
//   GRILLE                bands.low, et une légère pulsation sur le temps
//   FENÊTRE               une lumière douce constante
//   DIVERS, AMBIANCE      le meuble et le sol suivent la moyenne
// À pleine intensité et teinte 0 (le drop, ?jukezone=tout), l'image est
// exactement jukebox-on.jpeg (off + diff = on, construit au build).
//
// LE DISQUE : le cercle unité envoyé sur l'ellipse MESURÉE de la platine
// (homographie du build, centre = l'axe). Vinyle noir aux sillons fins (fondus
// quand ils passent sous le pixel), deux reflets de la fenêtre, la pochette en
// étiquette (46 % du rayon, mesuré), 45 tours par minute. Le bras reste devant
// (masque A du build) ; les reflets de la vitre qui traversent le disque sont
// reposés par-dessus (aucun sur ces photos : la couche est vide).
//
// LES BULLES (le détail décisif) : une petite simulation CPU par segment de
// tube (4 segments mesurés) : naissance au fond au débit des graves,
// tailles variées (petites surtout), vitesse propre irrégulière qui croît
// vers le haut, léger balancement, disparition au sommet. Chaque bulle est un
// quad instancié dont la position vient d'une texture de données réécrite à
// chaque image (aucun attribut) ; le fragment dessine un cercle par distance
// en pixels (R1) : bord brillant de 1 px teinté par le liquide lu sous elle,
// coeur transparent à peine éclairci, et se jette hors du masque du tube (B).
//
// LE CADRAGE (R18) : la photo couvre l'écran, ancrée sur le centre du
// juke-box (voir jukeboxLayout). Toutes les couches (diff, zones, disque,
// tubes, bras, reflets) sont lues en pixels de la photo par la même
// transformation : bulles et disque suivent le recadrage. LIMITE MESURÉE :
// sur cette photo le juke-box fait 96 % de la hauteur et son centre est à
// 68 % de la largeur ; couvrant un écran 16:9 il ne peut ni sortir de la
// pochette ni éviter les zones (voir le test) - il faut une photo élargie.

const TIER = { 0: { bubbles: 32 }, 1: { bubbles: 48 }, 2: { bubbles: 64 } };
const NO_BPM = 118;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const DROP_BEATS = 16;
const RPM45 = 45 / 60;                     // tours par seconde
const COVER_MARGIN = 0.05, FRAME_MARGIN = 0.10, ZONE_TOP = 0.76, ZONE_BOTTOM = -0.80, ZONE_PAD = 0.03;
const SAFE_FLOOR = 0.45;
// la roue des couleurs : teintes chaudes et saturées ; la première est la photo
const WHEEL = [null, 0xff3018, 0xffa020, 0xff3c8c, 0xffd040, 0xff6a10];
const WINDOW_I = 0.80, TUBE_I = 0.92, MISC_I = 0.70;
// les bulles (unités : pixels de la photo, secondes)
const BUBBLE_BASE_RATE = 0.9, BUBBLE_BASS_RATE = 4.0, BUBBLE_TORRENT = 5.0;
const ZONE_NAMES = { fenetre: 1, 'arche-g': 2, 'arche-d': 3, arche: 23, panneaux: 23, 'tube-g': 4, 'tube-d': 5, tubes: 45, grille: 6, divers: 7, ambiance: 8, tout: 99 };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
export function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// LE CADRAGE (R18) : la photo COUVRE l'écran (échelle = max des deux
// rapports, jamais « contenir » : ni bande noire ni fondu), et le recadrage
// est ancré sur le POINT D'INTÉRÊT, le centre de la boîte du juke-box : on
// vise le milieu de la place à droite de la pochette (sans que le juke-box
// sorte du cadre : entier d'abord), entre les zones du haut et du bas, puis
// on borne le décalage pour que la photo couvre toujours.
// k = unités d'écran par pixel de photo ; (ox, oy) = l'écran du pixel (0, 0),
// y de la photo vers le bas. `violations` mesure ce que ce cadrage ne tient
// pas (pochette, cadre, zones d'exclusion de R6).
export function jukeboxLayout(A, sc, sh, box, size) {
  const [W, H] = size;
  const k = Math.max(2 * A / W, 2 / H);
  const inner = Math.max(sc.x + sh.x, sh.x - sc.x) + COVER_MARGIN;
  const outer = A - FRAME_MARGIN;
  const top = ZONE_TOP - ZONE_PAD, bottom = ZONE_BOTTOM + ZONE_PAD;
  const cx = (box[0] + box[2]) / 2, cy = (box[1] + box[3]) / 2;
  // la cible : à droite de la pochette, mais d'abord ENTIER dans le cadre
  const hw = (box[2] - box[0]) * k / 2;
  const tx = Math.max(-A + FRAME_MARGIN + hw, Math.min(A - FRAME_MARGIN - hw, (inner + outer) / 2));
  let ox = tx - cx * k, oy = (top + bottom) / 2 + cy * k;
  ox = Math.max(A - W * k, Math.min(-A, ox));
  oy = Math.max(1, Math.min(-1 + H * k, oy));
  const b = [ox + box[0] * k, oy - box[1] * k, ox + box[2] * k, oy - box[3] * k];
  return { k, ox, oy, box: b, violations: layoutViolations(A, sc, sh, b) };
}
// ce que la boîte du juke-box (x0, haut, x1, bas, en unités) chevauche :
// la pochette (+ marge), le cadre (marge 5 %), les quatre zones de R6
export function layoutViolations(A, sc, sh, b) {
  const [x0, yT, x1, yB] = b, out = {};
  const overlap = (rx0, ry0, rx1, ry1) => { const w = Math.min(x1, rx1) - Math.max(x0, rx0), h = Math.min(yT, ry1) - Math.max(yB, ry0); return w > 0 && h > 0 ? +Math.min(w, h).toFixed(3) : 0; };
  const cov = overlap(sc.x - sh.x - COVER_MARGIN, sc.y - sh.y - COVER_MARGIN, sc.x + sh.x + COVER_MARGIN, sc.y + sh.y + COVER_MARGIN);
  if (cov) out.pochette = cov;
  const m = FRAME_MARGIN;
  const frame = Math.max(0, -A + m - x0, x1 - (A - m), yT - (1 - m), -1 + m - yB);
  if (frame > 0) out.cadre = +frame.toFixed(3);
  const z = { broadcast: overlap(A - 0.44 * A, 0.76, A, 1), micro: overlap(-A, 0.76, -A + 0.16 * A, 1), navigation: overlap(A - 0.44 * A, -1, A, -0.80), station: overlap(-0.4 * A, 0.80, 0.4 * A, 1) };
  for (const n in z) if (z[n]) out[n] = z[n];
  return out;
}

// le chenillard : intensité de l'arche à la coordonnée v quand le front est en f
export function chase(v, f) {
  const head = Math.exp(-Math.pow((v - f) / 0.07, 2));
  const trail = v <= f ? 0.55 * (1 - (f - v)) : 0;
  return 0.22 + 0.78 * Math.min(1, Math.max(head, trail));
}

const VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// highp : on travaille en pixels de photo (jusqu'à 1376) et le disque est
// petit ; mediump n'y suffit pas
const FRAGMENT = `
  precision highp float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform sampler2D uIds;
  uniform sampler2D uRefl;
  uniform sampler2D uCover;
  uniform vec2 uImg;
  uniform float uK;              // unités d'écran par pixel de photo
  uniform vec2 uO;               // l'écran du pixel (0, 0)
  uniform vec4 uZA;              // intensités : fenêtre, arche G, arche D, tube G
  uniform vec4 uZB;              // tube D, grille, divers, ambiance
  uniform float uFront;          // le front du chenillard (0..1)
  uniform float uChaseOn;
  uniform vec3 uHueA;            // la teinte des panneaux (luminance 1)
  uniform float uHueMix;         // 0 : la photo, 1 : la teinte
  uniform mat3 uHinv;            // pixel de photo -> cercle du disque
  uniform float uLabelR;
  uniform float uAngle;          // la rotation du disque
  uniform float uMask;           // ?jukemask=1
  uniform float uSimA;           // ?jukeaspect : le format simulé (0 : l'écran)
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  float chase(float v, float f) {
    float head = exp(-pow((v - f) / 0.07, 2.0));
    float trail = v <= f ? 0.55 * (1.0 - (f - v)) : 0.0;
    return 0.22 + 0.78 * min(1.0, max(head, trail));
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uSafeAspect, 1.0);
    vec2 px = vec2(s.x - uO.x, uO.y - s.y) / uK;
    vec2 uv = vec2(px.x / uImg.x, 1.0 - px.y / uImg.y);
    uv = clamp(uv, vec2(0.0), vec2(1.0));          // la photo couvre : ceci n'est qu'un garde-fou d'arrondi
    // R18 : aucun fondu de bord ; ?jukeaspect=<w>:<h> (débogage) ne montre que
    // la fenêtre d'un autre format, centrée
    if (uSimA > 0.0 && abs(s.x) > uSimA) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    float edge = 1.0;
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv).rgb;
    vec4 id = texture2D(uIds, vec2((floor(px.x) + 0.5) / uImg.x, 1.0 - (floor(px.y) + 0.5) / uImg.y));
    float z = floor(id.r * 255.0 + 0.5);
    vec4 wa = vec4(equal(vec4(z), vec4(1.0, 2.0, 3.0, 4.0)));
    vec4 wb = vec4(equal(vec4(z), vec4(5.0, 6.0, 7.0, 8.0)));
    float I = dot(wa, uZA) + dot(wb, uZB);
    bool panel = z > 1.5 && z < 3.5;
    if (panel) {
      I *= mix(1.0, chase(id.g, uFront), uChaseOn);
      float l = dot(diff, vec3(0.2126, 0.7152, 0.0722));
      diff = mix(diff, uHueA * l, uHueMix);
    }
    vec3 col = off + diff * I;
    // le disque
    vec3 h = uHinv * vec3(px, 1.0);
    vec2 d = h.xy / h.z;
    float r = length(d);
    float fw = max(fwidth(r), 1e-4);
    float inDisc = 1.0 - smoothstep(1.0 - fw, 1.0 + fw, r);
    if (inDisc > 0.0) {
      float lightW = 0.45 + 0.55 * uZA.x;
      vec3 disc;
      if (r < uLabelR) {
        float c = cos(uAngle), sn = sin(uAngle);
        vec2 q = mat2(c, sn, -sn, c) * d / uLabelR;
        disc = texture2D(uCover, q * 0.5 + 0.5).rgb * 0.85 * lightW;
        // le trou et l'axe chromé
        float hole = 1.0 - smoothstep(0.07, 0.12, length(q));
        disc = mix(disc, vec3(0.55, 0.53, 0.50) * lightW, hole);
        // le bord de l'étiquette
        disc *= 1.0 - 0.3 * smoothstep(0.90, 1.0, length(q));
      } else {
        // le vinyle : sillons concentriques (fondus sous le pixel), deux
        // reflets de la fenêtre aux bords gauche et droit, un liseré au bord
        float gr = r * 70.0;
        float gw = fwidth(gr);
        float groove = mix(0.5 + 0.5 * cos(6.2831853 * gr), 0.5, smoothstep(0.3, 0.6, gw));
        float phi = atan(d.y, d.x);
        float sheen = pow(abs(cos(phi)), 24.0) * smoothstep(uLabelR, uLabelR + 0.2, r);
        disc = vec3(0.012) + vec3(1.0, 0.92, 0.80) * lightW * (0.05 * groove + 0.28 * sheen * (0.6 + 0.4 * groove));
        disc += vec3(0.10) * lightW * smoothstep(0.94, 0.99, r);
      }
      // les reflets de la vitre par-dessus (prémultipliés)
      vec4 rf = texture2D(uRefl, uv);
      disc = disc * (1.0 - rf.a) + rf.rgb;
      // le bras reste devant
      col = mix(col, disc, inDisc * (1.0 - id.a));
    }
    if (uMask > 0.5) {
      vec3 zc = z < 0.5 ? vec3(0.0) : (z < 1.5 ? vec3(0.3, 0.6, 1.0) : (z < 2.5 ? vec3(1.0, 0.25, 0.25) : (z < 3.5 ? vec3(1.0, 0.8, 0.15) : (z < 4.5 ? vec3(0.25, 1.0, 0.5) : (z < 5.5 ? vec3(0.15, 0.85, 0.85) : (z < 6.5 ? vec3(1.0) : (z < 7.5 ? vec3(0.7, 0.4, 1.0) : vec3(0.35))))))));
      if (panel) zc *= 0.3 + 0.7 * id.g;
      if (id.b > 0.0) zc = mix(zc, vec3(1.0, 0.0, 1.0), 0.6);
      zc = mix(zc, vec3(0.0, 1.0, 0.0), inDisc * 0.5);
      zc = mix(zc, vec3(1.0, 0.5, 0.0), id.a);
      col = mix(off, zc, 0.7);
    }
    gl_FragColor = vec4(col * edge * safeAtt(s), 1.0);
  }
`;

const BUBBLE_VERTEX = `
  precision highp float;
  attribute float aIndex;
  uniform sampler2D uData;       // x, y, rayon (px de photo), tube + fondu
  uniform float uCount;
  uniform float uK;
  uniform vec2 uO;
  uniform float uSafeAspect;
  uniform float uPxPerUnit;      // pixels d'écran par unité
  varying vec2 vLocal;           // en pixels d'écran depuis le centre
  varying float vR;              // rayon en pixels d'écran
  varying float vTube;
  varying float vFade;
  varying vec2 vPx;              // pixel de photo
  void main() {
    vec4 b = texture2D(uData, vec2((aIndex + 0.5) / uCount, 0.5));
    if (b.z <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float rs = b.z * uK * uPxPerUnit;              // le rayon à l'écran
    float ext = rs + 2.0;                          // le quad : rayon + 2 px (rien au-delà du bord)
    vLocal = position.xy * ext; vR = rs;
    vTube = floor(b.w); vFade = fract(b.w) * 1.0001;
    vec2 c = vec2(uO.x + b.x * uK, uO.y - b.y * uK);
    vec2 sc = c + vLocal / uPxPerUnit;
    vPx = b.xy + vec2(vLocal.x, -vLocal.y) / (uK * uPxPerUnit);
    gl_Position = vec4(sc.x / uSafeAspect, sc.y, 0.0, 1.0);
  }
`;
const BUBBLE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform sampler2D uIds;
  uniform vec2 uImg;
  uniform float uTubeI;
  uniform float uDebugOnly;
  varying vec2 vLocal;
  varying float vR;
  varying float vTube;
  varying float vFade;
  varying vec2 vPx;
  void main() {
    // jamais hors du masque du tube
    vec4 id = texture2D(uIds, vec2((floor(vPx.x) + 0.5) / uImg.x, 1.0 - (floor(vPx.y) + 0.5) / uImg.y));
    if (abs(id.b * 255.0 - (vTube + 1.0)) > 0.5) discard;
    float d = length(vLocal);                      // pixels d'écran
    float rim = 1.0 - smoothstep(0.5, 1.4, abs(d - vR + 0.6));   // le bord : ~1 px
    float core = 1.0 - smoothstep(vR - 1.0, vR, d);
    if (rim <= 0.0 && core <= 0.0) discard;
    vec2 uv = vec2(vPx.x / uImg.x, 1.0 - vPx.y / uImg.y);
    vec3 liquid = texture2D(uOff, uv).rgb + texture2D(uDiff, uv).rgb * uTubeI;
    // bord brillant éclairé par le liquide, coeur transparent à peine plus clair
    vec3 c = (liquid * 0.9 + vec3(0.22)) * rim + liquid * 0.12 * core * (1.0 - rim);
    if (uDebugOnly > 0.5) c = vec3(1.0) * max(rim, core);
    gl_FragColor = vec4(c * vFade, 1.0);
  }
`;

// la pochette de repli : un dégradé de la palette (R11)
function fallbackCover(palette) {
  const src = palette && (palette.vividColors || palette.dominantColors) || [];
  const a = vdjColor(src[0] != null ? src[0] : 0xff3c8c, new THREE.Color()), b = vdjColor(src[1] != null ? src[1] : 0xffb020, new THREE.Color());
  const N = 32, data = new Uint8Array(N * N * 4), c = new THREE.Color();
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    c.copy(a).lerp(b, j / (N - 1)).convertLinearToSRGB();
    const o = (j * N + i) * 4; data[o] = c.r * 255; data[o + 1] = c.g * 255; data[o + 2] = c.b * 255; data[o + 3] = 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}
function wheelColour(hex) {
  const c = vdjColor(hex, new THREE.Color());
  const l = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  return new THREE.Vector3(c.r / l, c.g / l, c.b / l);
}

// =============================================================================

export class JukeboxVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._L = TIER[this._tier];
    this._mask = urlParam('jukemask') === '1';
    const z = (urlParam('jukezone') || '').toLowerCase();
    this._zone = z && ZONE_NAMES[z] != null ? ZONE_NAMES[z] : null;
    const asp = (urlParam('jukeaspect') || '').split(':').map(Number);
    this._simA = asp.length === 2 && asp[0] > 0 && asp[1] > 0 ? asp[0] / asp[1] : 0;
    const f = parseFloat(urlParam('jukebubbles'));
    this._bubbleRate = Number.isFinite(f) ? Math.max(0, f) : null;
    this._post = { bloomStrength: 0.20, bloomThreshold: 0.86, grain: 0.01, vignette: 0.25, aberration: 0.0006, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    this._comfort = new VdjComfort();
    if (urlParam('jukeprobe') === '1' && typeof window !== 'undefined') window.__jukebox = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._assets = getJukebox();
    if (!this._assets) { this._log = [{ what: 'indisponible' }]; return; }   // l'orchestrateur l'a déjà retiré
    const A = this._assets, D = A.data;
    this._data = D;
    const safe = { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._fallback = fallbackCover(null);
    const Hi = D.record.Hinv;
    this._U = Object.assign({
      uOff: { value: A.off }, uDiff: { value: A.diff }, uIds: { value: A.ids }, uRefl: { value: A.refl }, uCover: { value: this._fallback },
      uImg: { value: new THREE.Vector2(D.size[0], D.size[1]) }, uK: { value: 0.002 }, uO: { value: new THREE.Vector2() },
      uZA: { value: new THREE.Vector4(1, 1, 1, 1) }, uZB: { value: new THREE.Vector4(1, 1, 1, 1) },
      uFront: { value: 0 }, uChaseOn: { value: 1 }, uHueA: { value: new THREE.Vector3(1, 1, 1) }, uHueMix: { value: 0 },
      uHinv: { value: new THREE.Matrix3().set(Hi[0], Hi[1], Hi[2], Hi[3], Hi[4], Hi[5], Hi[6], Hi[7], Hi[8]) },
      uLabelR: { value: D.record.labelR }, uAngle: { value: 0 }, uMask: { value: this._mask ? 1 : 0 }, uSimA: { value: this._simA || 0 }
    }, safe);
    this._mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: VERTEX, fragmentShader: FRAGMENT, depthTest: false, depthWrite: false }));
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -3000;
    scene.add(this._mesh);

    // les bulles : quads instanciés, positions dans une texture de données
    const M = this._L.bubbles;
    this._bData = new Float32Array(M * 4);
    this._bTex = new THREE.DataTexture(this._bData, M, 1, THREE.RGBAFormat, THREE.FloatType);
    this._bTex.minFilter = this._bTex.magFilter = THREE.NearestFilter; this._bTex.generateMipmaps = false; this._bTex.needsUpdate = true;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const idx = new Float32Array(M); for (let i = 0; i < M; i++) idx[i] = i;
    g.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1));
    g.instanceCount = M;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._BU = {
      uData: { value: this._bTex }, uCount: { value: M }, uK: this._U.uK, uO: this._U.uO, uSafeAspect: safeZoneUniforms.uAspect,
      uPxPerUnit: { value: 540 }, uOff: { value: A.off }, uDiff: { value: A.diff }, uIds: { value: A.ids }, uImg: this._U.uImg,
      uTubeI: { value: TUBE_I }, uDebugOnly: { value: 0 }
    };
    this._bubbleMesh = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._BU, vertexShader: BUBBLE_VERTEX, fragmentShader: BUBBLE_FRAGMENT, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending }));
    this._bubbleMesh.frustumCulled = false; this._bubbleMesh.renderOrder = -2990;
    const res = new THREE.Vector2();
    this._bubbleMesh.onBeforeRender = (renderer) => { const t = renderer.getRenderTarget(); if (t) res.set(t.width, t.height); else renderer.getDrawingBufferSize(res); this._BU.uPxPerUnit.value = res.y / 2; };
    if (!this._mask) scene.add(this._bubbleMesh);

    // l'état
    this._r = rng(4242);
    this._tubes = D.tubes.map((t, k) => ({ k, x0: t.x0, x1: t.x1, y0: t.y0, y1: t.y1, acc: this._r() * 0.5, list: [] }));
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._low = 0.3; this._angle = 0;
    this._wheel = 0; this._wheelFrom = 0; this._wheelAt = -99;
    this._drop = null; this._lastMode = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._artUrl = undefined; this._coverTex = null;
    this._log = [];
    this._stats = { spawned: 0, drops: 0, phrases: 0 };
  }

  _cover(profile) {
    const url = profile.artworkUrl || null;
    if (url === this._artUrl) return;
    this._artUrl = url;
    if (this._fallback) this._fallback.dispose();
    this._fallback = fallbackCover(profile.artworkPalette);
    if (!this._coverTex) this._U.uCover.value = this._fallback;
    if (!url) { if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; } this._U.uCover.value = this._fallback; return; }
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!this._scene || url !== this._artUrl) { tex.dispose(); return; }
      if (this._coverTex) this._coverTex.dispose();
      this._coverTex = tex; this._U.uCover.value = tex;
    }, () => { if (this._scene && url === this._artUrl) this._U.uCover.value = this._fallback; });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  update(deltaTime, profile) {
    if (!this._assets) return;
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._cover(profile);
    this._layout();
    if (profile.playbackState !== 'PLAYING') return;          // pause : tout gèle (le disque aussi)
    this._time += dt;

    // l'horloge, en temps (Domino, Twist)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock;
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    // les graves : bands.low lissé ; sans analyse, une pulsation synthétique
    const director = profile.director;
    const bands = director && director.bands;
    const beatPhase = clock - Math.floor(clock);
    const lowIn = bands && director.hasReliableGrid ? clamp01(bands.low) : 0.35 + 0.35 * Math.exp(-beatPhase * 5);
    this._low += (lowIn - this._low) * (1 - Math.exp(-dt / 0.12));

    // DROP
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && (!this._drop || clock - this._drop.start > 4)) { this._drop = { start: clock }; this._stats.drops++; if (this._comfort.requestFlash(this._time)) this._flash = 1; }
    if (this._drop && clock - this._drop.start >= DROP_BEATS) this._drop = null;

    // PHRASE : la roue des couleurs avance d'un cran, en une mesure
    if (phrase && !this._drop) { this._wheelFrom = this._wheel; this._wheel = (this._wheel + 1) % WHEEL.length; this._wheelAt = clock; this._stats.phrases++; }

    // le disque : 45 tours par minute, en temps réel
    this._angle = (this._angle + dt * RPM45 * Math.PI * 2) % (Math.PI * 2);
    this._U.uAngle.value = this._angle;

    this._lights(clock, beatPhase);
    this._bubbles(dt, clock);
  }

  // la teinte courante des panneaux : [couleur, mélange]
  _wheelMixed() {
    const u = clamp01((this._clock - this._wheelAt) / 4);
    return { from: this._wheelFrom, to: this._wheel, u };
  }

  _lights(clock, beatPhase) {
    const U = this._U;
    const drop = !!this._drop;
    const pulse = 0.12 * Math.exp(-beatPhase * 6);
    let grille = clamp01(0.30 + 0.60 * this._low + pulse);
    let arch = 1, tubes = TUBE_I, win = WINDOW_I, misc = MISC_I;
    U.uFront.value = clock / 4 - Math.floor(clock / 4);
    U.uChaseOn.value = 1;
    // la teinte : fondu d'une mesure entre deux crans de la roue (0 = la photo)
    const w = this._wheelMixed();
    const col = (k) => k === 0 || WHEEL[k] == null ? null : wheelColour(WHEEL[k]);
    const a = col(w.from), b = col(w.to), u = w.u;
    // mélange : la teinte n'existe que hors de la photo ; on interpole couleur ET mélange
    const ma = a ? 1 : 0, mb = b ? 1 : 0;
    const ca = a || b || new THREE.Vector3(1, 1, 1), cb = b || a || new THREE.Vector3(1, 1, 1);
    U.uHueA.value.copy(ca).lerp(cb, u);
    U.uHueMix.value = ma + (mb - ma) * u;
    if (drop) { grille = 1; arch = 1; tubes = 1; win = 1; misc = 1; U.uChaseOn.value = 0; U.uHueMix.value = 0; }
    const ambient = (grille + arch * 0.6 + tubes + win) / 4;
    let ZA = [win, arch, arch, tubes], ZB = [tubes, grille, misc, ambient];
    // ?jukezone=<zone> : cette zone seule, à pleine intensité, teinte de la photo
    if (this._zone != null) {
      const on = (n) => this._zone === 99 || this._zone === n || (this._zone === 23 && (n === 2 || n === 3)) || (this._zone === 45 && (n === 4 || n === 5)) ? 1 : 0;
      ZA = [on(1), on(2), on(3), on(4)]; ZB = [on(5), on(6), on(7), on(8)];
      U.uChaseOn.value = 0; U.uHueMix.value = 0;
    }
    U.uZA.value.set(ZA[0], ZA[1], ZA[2], ZA[3]);
    U.uZB.value.set(ZB[0], ZB[1], ZB[2], ZB[3]);
    this._BU.uTubeI.value = ZA[3];
  }

  // LES BULLES : naissance au fond au débit des graves, montée irrégulière
  // qui accélère, balancement, fin au sommet
  _bubbles(dt, clock) {
    const r = this._r;
    const M = this._L.bubbles, perTube = Math.floor(M / this._tubes.length);
    const torrent = this._drop ? BUBBLE_TORRENT : 1;
    const rate = this._bubbleRate != null ? this._bubbleRate : (BUBBLE_BASE_RATE + BUBBLE_BASS_RATE * this._low) * torrent;
    for (const t of this._tubes) {
      const w = t.x1 - t.x0, len = t.y1 - t.y0;
      // naissances : un processus de Poisson
      t.acc -= dt * rate;
      while (t.acc <= 0) {
        t.acc += -Math.log(1 - r() * 0.999);
        if (t.list.length >= perTube) continue;
        const u = r();
        const rad = Math.min(w / 2 - 1.5, 1.3 + 3.0 * u * u * u + 0.4 * r());   // petites surtout
        if (rad < 1) continue;
        t.list.push({ x: t.x0 + rad + 1 + r() * Math.max(0, w - 2 * rad - 2), y: t.y1 - rad, r: rad, v0: (22 + 30 * r()) * (this._drop ? 1.5 : 1), seed: r() * 100, age: 0 });
        this._stats.spawned++;
      }
      for (const b of t.list) {
        b.age += dt;
        const s = clamp01((t.y1 - b.y) / len);
        // vitesse propre, irrégulière (deux sinus de périodes premières), qui croît vers le haut
        const irr = 1 + 0.30 * Math.sin(b.seed + b.age * 2.3) * Math.sin(b.seed * 1.7 + b.age * 0.9);
        b.y -= dt * b.v0 * (1 + 0.9 * s) * irr;
        b.x += Math.sin(b.seed * 3 + b.age * 3.1) * 0.25 * dt * 10;
        b.x = Math.max(t.x0 + b.r + 1, Math.min(t.x1 - b.r - 1, b.x));
      }
      t.list = t.list.filter((b) => b.y > t.y0 + b.r);
    }
    // la texture : tube + fondu (entrée sur 4 px, sortie sur les 6 % du haut)
    const D = this._bData; D.fill(0);
    let i = 0;
    for (const t of this._tubes) for (const b of t.list) {
      if (i >= M) break;
      const len = t.y1 - t.y0, s = (t.y1 - b.y) / len;
      const fade = Math.min(1, (t.y1 - b.y) / 4) * (1 - clamp01((s - 0.94) / 0.06));
      D[i * 4] = b.x; D[i * 4 + 1] = b.y; D[i * 4 + 2] = b.r; D[i * 4 + 3] = t.k + Math.min(0.999, Math.max(0, fade));
      i++;
    }
    this._bTex.needsUpdate = true;
  }

  _layout() {
    const real = safeZoneUniforms.uAspect.value || 16 / 9;
    const A = this._simA && this._simA < real ? this._simA : real;
    // R19 : le cadrage se rapporte au centre de composition (le bloc pochette
    // tel qu'il serait au centre), pas à la position de la pochette
    const L = jukeboxLayout(A, safeZoneUniforms.uStageCenter.value, safeZoneUniforms.uStageHalfSize.value, this._data.box, this._data.size);
    this._U.uK.value = L.k; this._U.uO.value.set(L.ox, L.oy);
    this._lay = L;
  }

  getProbe() {
    return { tier: this._tier, clock: +this._clock.toFixed(3), front: this._U ? +this._U.uFront.value.toFixed(3) : null, wheel: this._wheel, drop: !!this._drop,
      low: +this._low.toFixed(3), bubbles: this._tubes ? this._tubes.map((t) => t.list.length) : null, layout: this._lay, stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._scene) {
      if (this._mesh) this._scene.remove(this._mesh);
      if (this._bubbleMesh) this._scene.remove(this._bubbleMesh);
      this._scene.background = this._prevBackground || null;
    }
    for (const m of [this._mesh, this._bubbleMesh]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._bTex) this._bTex.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._fallback) this._fallback.dispose();
    if (typeof window !== 'undefined' && window.__jukebox === this) window.__jukebox = null;
    this._scene = null;
  }
}
